import React from "react";
import { Card } from "@campgladiator/cgui-core.atoms.card";
import { TableGroup } from "./components/table-group";
import { TableHeader } from "./components/table-header";
import { TableFooter } from "./components/table-footer";
import { TableManualProps, TableData } from "./table.d";
import { useTable } from ".";
import styles from "./table.module.scss";
import { TableHeaderProps } from "./components/table-header/table-header";

export type TableAutomaticProps = {
  /**
   * Controls the currently selected page in the table footer's pagination. It is suggested that you use a state variable to handle this value.
   */
  currentPage?: number;
  /**
   * This is an array of buttons that can be added in the header
   */
  buttons?: TableHeaderProps["buttons"];
  /**
   * Use this callback function prop to take action when the user clicks a page number in the table footer's pagination. Typically, this will be used to set state with the new page number as well as handle network calls to get data for the newly selected page.
   */
  onPageChange?: (page: number) => void;
  /**
   * Control whether or not you want to display the table footer/pagination
   */
  showFooter?: boolean;
  /**
   * Used to pass in the data that will populate the table
   */
  tableData?: TableData;
  /**
   * Allows you to set the name of the table that will be displayed in the table header.
   */
  tableName: string;
  /**
   * Allows you to set the subtitle of the table that will be displayed in the table header.
   */
  tableSubTitle?: string;
  /**
   * Controls the total number of pages you want to be shown in the table footer pagination.
   */
  totalPageCount?: number;

  /**
   * Controls the icon name you want to be shown in the table header.
   */
  iconName?: string;
};

export const Automatic = ({
  currentPage = 1,
  buttons,
  onPageChange = (page: number) => {},
  tableData,
  tableName,
  totalPageCount = 1,
  showFooter,
  tableSubTitle,
  iconName,
}: TableAutomaticProps) => {
  const { paginationSize, deviceWidth } = useTable();

  return (
    <Card flat className={styles.container}>
      <TableHeader
        deviceWidth={deviceWidth}
        tableName={tableName}
        buttons={buttons}
        tableSubTitle={tableSubTitle}
        iconName={iconName}
      />
      {tableData && <TableGroup data={tableData} />}
      {tableData && showFooter && (
        <TableFooter
          onPageChange={onPageChange}
          currentPage={currentPage}
          totalPageCount={totalPageCount}
          size={paginationSize}
        />
      )}
    </Card>
  );
};

export const Manual = ({
  children,
  currentPage = 1,
  buttons,
  onPageChange = (page: number) => {},
  tableName,
  totalPageCount = 1,
  showFooter,
  tableSubTitle,
  tableCornerTitle,
  iconName,
}: TableManualProps) => {
  const { paginationSize, deviceWidth } = useTable();

  return (
    <Card flat className={styles.container}>
      <TableHeader
        deviceWidth={deviceWidth}
        tableName={tableName}
        iconName={iconName}
        tableSubTitle={tableSubTitle}
        buttons={buttons}
        tableCornerTitle={tableCornerTitle}
      />
      <TableGroup>{children}</TableGroup>
      {showFooter && (
        <TableFooter
          onPageChange={onPageChange}
          currentPage={currentPage}
          totalPageCount={totalPageCount}
          size={paginationSize}
        />
      )}
    </Card>
  );
};

export default { Automatic, Manual };
